import classNames from 'classnames';
import { omit } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Card, CardBody, CardHeader } from "reactstrap";
import Button from '../../../components/Button';
import DateRangeFilters from '../../../components/DateRangeFilters';
import { useDateFilters } from '../../../components/DateRangeFilters/useDateFilters';
import NTable from '../../../components/NTable';
import { useNewTable } from '../../../components/NTable/hook';
import Pagination from '../../../components/NTable/Pagination';
import { useAccess } from '../../../utils/hooks/access';
import { useInitialState } from "../../../utils/hooks/useInitialState";
import { useGenerateReport, useGetReport } from "../api";
import { useGenerateModal } from '../ReportTable/useGenerateModal';
import Filters from './Filters';
import { useColumns } from './useColumns';
import { useBindPayGroupWithTable } from './useBindPayGropWithTable';

const LIMIT = 1200;

const TableView = ({ values, isPreview, onReportLoaded }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { hasAccess: canExport } = useAccess("reports.canExport");
  const { hasAccess: canEdit } = useAccess("reports.canEdit");
  const { hasAccess: canCreate } = useAccess("reports.canCreate");
  const navigate = useNavigate();
  const location = useLocation();
  const { open, modal } = useGenerateModal();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [offset, setOffset] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const { from, to, mode, payGroup } = useDateFilters();

  const {
    generateReport,
    generate,
    data: generatedData,
    extraColumns,
    totalItems,
    loading,
  } = useGenerateReport();

  const { getReport, report, loading: reportLoading } = useGetReport();

  const processFilterValues = (values) => {
    if (!values) return "";

    const processValue = (v) => {
      if (v == null) return null;

      if (v instanceof Date || moment.isMoment(v)) {
        return moment(v).format("YYYY-MM-DD");
      }

      if (typeof v === "object") {
        if (v.id) {
          return {
            id: v?.id,
            code: v?.code,
            description: v?.description,
            glSegment: v?.glSegment,
            name: v?.name,
          }
        }
        return null;
      }

      if (typeof v === "string") {
        // if (moment(v, [moment.ISO_8601, "YYYY-MM-DD"], true).isValid()) {
        //   return moment(v).format("YYYY-MM-DD");
        // }
        return v;
      }

      return String(v);
    };

    if (Array.isArray(values)) {
      return values.map(processValue).filter(Boolean);
    }

    if (typeof values === "string" && values.includes(",")) {
      return values
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }

    const result = processValue(values);
    return result ? [result] : "";
  };

  const generateSegment = useCallback((itemsPerPage, page, totalItems) => {
    const segmentSize = Math.ceil(LIMIT / itemsPerPage);
    const totalSegments = Math.ceil(totalItems / itemsPerPage);
    const segmentIndex = Math.floor(page / segmentSize);
    const minPage = segmentIndex * segmentSize;
    const maxPage = Math.min(minPage + segmentSize - 1, totalSegments - 1);
    return { minPage, maxPage };
  }, []);

  const [segment, setSegment] = useState({ minPage: 0, maxPage: 59 });

  const goToPage = useCallback((value) => {
    setPage(value);
    if (!(value <= segment.maxPage && value >= segment.minPage)) {
      const generatedSegment = generateSegment(perPage, value, totalItems);
      setSegment(generatedSegment);
      setOffset(generatedSegment.minPage * perPage);
    }
  }, [segment, generateSegment, perPage, totalItems]);

  const onPerPageChange = useCallback((value) => {
    setPerPage(value);
    setPage(0);
    const generatedSegment = generateSegment(value, 0, totalItems);
    if (generatedSegment.minPage !== segment.minPage || generatedSegment.maxPage !== segment.maxPage) {
      setSegment(generatedSegment);
      setOffset(generatedSegment.minPage * value);
    }
  }, [generateSegment, segment.maxPage, segment.minPage, totalItems]);

  const handleEdit = useMemo(() => (reportId) => {
    navigate(`/reports/edit/${reportId}`, {
      state: { parentId: report?.parentId ?? location.state?.parentId ?? null },
    });
  }, [navigate, report?.parentId, location.state?.parentId]);

  const handleClone = useMemo(() => (reportId) => {
    navigate(`/reports/clone/${reportId}`, {
      state: { parentId: report?.parentId ?? location.state?.parentId ?? null },
    });
  }, [navigate, report?.parentId, location.state?.parentId]);

  const columns = useColumns({
    config: useMemo(() => {
      return !values ? report?.reportData?.columns : (values?.dimensions || [])
    }, [report, values]),
    extraColumns
  });

  const findPageIndex = useCallback(() => {
    const values = [];
    for (let i = segment.minPage; i <= segment.maxPage; i++) {
      values.push(i);
    }
    return values.indexOf(page);
  }, [segment, page]);

  const data = useMemo(() => {
    if (!generatedData || generatedData.length === 0) return [];
    const index = findPageIndex();
    return generatedData.slice(index * perPage, (index * perPage) + perPage);
  }, [findPageIndex, generatedData, perPage]);

  const table = useNewTable({
    data,
    columns,
    totalItems,
    loading: loading || reportLoading,
    enableRowSelection: false,
    enableSorting: false,
    initialState: useInitialState(() => {
      const columnFilters = [
        {
          operator: "contains",
          member: columns?.[0]?.name,
          values: "",
        },
      ];
      return { columnFilters };
    }),
    sessionFiltersKey: `report-${id}`,
  });

  const handleExportItem = useCallback(
    (item) => {
      const leafColumns = table.getAllLeafColumns();
      const excludedColumns = leafColumns
        .filter((column) => !column.getIsVisible())
        .map((column) => column.id);
      const exportColumns = leafColumns.map((column) => ({
        id: column.id,
        header:
          typeof column.columnDef.header === "string"
            ? column.columnDef.header
            : column.columnDef.columnToggleLabel || column.id,
      }));

      open({ ...item, type: "export", excludedColumns, exportColumns });
    },
    [open, table]
  );

  const onFiltersChange = useCallback((values) => {
    setHasChanges(true);
    const data = values?.map((value) => ({
      ...value,
      values: processFilterValues(value?.values)
    }));
    table.setColumnFilters(data);
    setOffset(0);
  }, [table]);

  const customFilters = table.getState().columnFilters;

  useEffect(() => {
    if (!from || !to) return;

    const controller = new AbortController();
    const shouldSetUndefined = customFilters?.every(filter =>
      !filter.member ||
      !filter.operator ||
      (Array.isArray(filter?.values) && filter?.values.every(value => !value))
    );

    const customFilterData = shouldSetUndefined
      ? undefined
      : customFilters?.map(filter => ({
        ...filter,
        values: !Array.isArray(filter?.values) ? [filter?.values] : filter?.values?.map((value) => value?.id || value),
        join: (filter?.join /**|| "AND" */)?.toUpperCase(),
      }));

    if (!from && !to) return;

    if (id && !values && !isPreview) {
      generateReport({
        reportId: id,
        limit: LIMIT,
        offset,
        total: true,
        customFilters: customFilterData,
        from: moment(from).format("YYYY-MM-DD"),
        to: moment(to).format("YYYY-MM-DD"),
      }, controller)
    } else {
      const dimensions = values?.dimensions?.map((dim) => omit(dim, ["relationships", "table"])) || [];
      if (values?.calculations?.length > 0) {
        const calculations = values.calculations.map((calc, i) => ({
          ...calc,
          dataIndex: "calculation_" + i
        }));
        dimensions.push(calculations);
      }

      const data = {
        ...values,
        filters: values?.filters?.map((filter, i) => ({
          member: filter.name,
          operator: filter.operator,
          values: processFilterValues(filter?.values),
          join: values?.filters?.[i - 1]?.join //|| "AND",
        })),
        customFilters: customFilterData,
        dimensions: dimensions?.flat(),
        limit: 10,
        offset: 0,
        total: true,
        order: values?.orders || [],
        measures: values?.measures?.map(measure => ({
          name: `${measure?.name}.${measure?.aggregation}`,
          alias: measure.alias || undefined,
        })) || [],
        union: values?.unions || [],
        from: moment(from).format("YYYY-MM-DD"),
        to: moment(to).format("YYYY-MM-DD"),
      }
      generate(omit(data, ["calculations"]), controller)
    }
    return () => controller.abort();
  }, [generate, generateReport, id, offset, customFilters, values, from, to, isPreview, t]);

  useEffect(() => {
    const controller = new AbortController();
    getReport(id, controller);
    return () => controller.abort();
  }, [id, getReport]);

  useEffect(() => {
    if (report && onReportLoaded && !values && !isPreview) {
      onReportLoaded(report);
    }
  }, [report, onReportLoaded, values, isPreview]);

  useEffect(() => {
    if (hasChanges) {
      table.setPageIndex(0);
      setPage(0);
      setHasChanges(false);
      if (!(0 <= segment.maxPage && 0 >= segment.minPage)) {
        const generatedSegment = generateSegment(perPage, 0, totalItems);
        setSegment(generatedSegment);
        setOffset(generatedSegment.minPage * perPage);
      }
    }
  }, [table.setPageIndex, setHasChanges, generateSegment, hasChanges, perPage, segment, table, totalItems])

  const tableFilters = useMemo(() => {
    const _filters = table.getState().columnFilters;
    const isValidDate = (value) => value instanceof Date || moment.isMoment(value);
    const isValidDateString = (value) => moment(value, "YYYY-MM-DD", true).isValid();

    const processedFilters = (values) => {
      if (Array.isArray(values)) {
        if (isValidDate(values?.[0]) || isValidDateString(values?.[0])) {
          return moment(values?.[0]).toDate();
        }
        return values;
      }

      if (isValidDate(values) || isValidDateString(values)) {
        return moment(values).toDate();
      }

      return values;
    };


    return _filters.map((filter) => {
      const processedValues = processedFilters(filter?.values);

      return {
        ...filter,
        values: processedValues,
      };
    });
  }, [table]);

  useBindPayGroupWithTable({
    filters: tableFilters,
    setFilters: onFiltersChange,
    payGroup,
    mode,
    report
  });

  return (
    <Card
      className={classNames(values && "shadow-none")}
      style={{ height: `calc(100% - 150px)` }}
    >
      <CardHeader className="d-flex justify-content-between align-items-center">
        <div className="d-flex gap-4 align-items-center">
          <h3 className="mb-0">{values?.name || report?.name}</h3>
          <div className="d-flex gap-2">
            {(!isPreview && !reportLoading && (values?.name || report?.name)) && (
              <>
                {(canEdit && !report?.isSystem && !report?.reportData?.isDefault) && (
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => handleEdit(id)}
                  >
                    {t("edit")}
                  </Button>
                )}

                {(canCreate && !report?.isSystem && !report?.reportData?.isDefault) && (
                  <Button
                    color="dark"
                    size="sm"
                    onClick={() => handleClone(id)}
                  >
                    {t("clone")}
                  </Button>
                )}
                {canExport && (
                  <Button
                    color="secondary"
                    size="sm" onClick={() => handleExportItem(report)}
                  >
                    {t("export")}
                  </Button>)}
              </>
            )}
          </div>
        </div>
        <DateRangeFilters setHasChangedDateFilter={setHasChanges} />
      </CardHeader>

      <CardBody
        className={classNames("p-0 m-0", values && "shadow-none")}
        style={{ height: "calc(100% - 150px)" }}
      >
        {!values && (
          <Filters
            report={report}
            filters={tableFilters}
            setFilters={onFiltersChange}
            columns={table.getAllLeafColumns()}
          />
        )}
        <NTable
          table={table}
          pagination={false}
          hasChangedDateFilter={hasChanges}
          setHasChangedDateFilter={setHasChanges}
        />
        {!values && (
          <Pagination
            totalItems={totalItems}
            pageCount={Math.ceil(totalItems / perPage)}
            page={page}
            perPage={perPage}
            showOnSizeChange
            gotoPage={goToPage}
            setPerPage={onPerPageChange}
          />
        )}
      </CardBody>
      {modal}
    </Card>
  );
}

export default TableView;
